import { Component, OnDestroy, OnInit } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filterNullOrUndefined } from "src/app/@operators/filter-null-or-undefined";
import { eKioskError } from "src/app/shared/models/api/constants";
import { AppState, getState, resetStates } from "src/app/state/app.state";
import * as ArcHospiActions from "../../archospi/state/archospi.actions";
import { ErrorLevel } from "../../error/error-level";
import { setErrorMsg } from "../../error/state/error.actions";
import { loadQuestionnaire } from "../../question/state/question.actions";
import { setTitle } from "../../title/state/title.actions";
import { setOverwritePreHospiConfirmed, setPrehospi } from "../../transaction-info/state/transaction-info.actions";
import { getHospitalization, getQuestionnaire } from "../../transaction-info/state/transaction-info.selectors";
import { environment } from "src/environments/environment";
import { CityDto, ContractDto, HospitalizationDto, InsurerDto, InsurerPreHospiAllowContinueEnum, MessageDto, QuestionType, RefLanguageDto, SiteDto } from "src/app/api/models";
import { MessageService } from "src/app/api/services";

@Component({
    selector: "app-check-prehospi",
    templateUrl: "./check-prehospi.component.html",
    styleUrls: ["./check-prehospi.component.scss"],
    standalone: false
})
export class CheckPreHospiComponent implements OnInit,OnDestroy {
	public site: SiteDto;
	public city: CityDto;
	public msgToShow: string;
	public insurer: InsurerDto;
	public language: RefLanguageDto;
	public hospi: HospitalizationDto;
	public InsurerPreHospiAllowContinueEnum = InsurerPreHospiAllowContinueEnum;
	public questiontree = [];
	public lightMessage: string | undefined = undefined;
	public displayButtons: boolean = true;
	private contract: ContractDto;
	private subs: Subscription[] = [];
	public lightColor: string;
	public existingPrehospiInsurerMessage: MessageDto = null;


	constructor(private store: Store<AppState>, private translateService: TranslateService, private messageService: MessageService) {
		this.subs.push(getState(this.store).subscribe(state => {
			this.contract = state.transactionInfo.selectedContract;
			this.site = state.transactionInfo.selectedSite;
			this.city = state.transactionInfo.selectedCity;
			this.insurer = state.transactionInfo.selectedInsurer;
			this.language = state.transactionInfo.selectedLanguage;
		}));	
	}

	ngOnInit(): void {
		this.store.dispatch(
			setTitle({
				data: "TITLE_CHECK_EXISTING_PREHOSPI",
				data2: undefined,
			})
		);
		if(this.insurer.existingPrehospiMessageId != null)
		{
			this.messageService.messageGet$Json({InsurerId: this.insurer.insurerId, MessageId: this.insurer.existingPrehospiMessageId }).subscribe(((message) =>
			{
				this.existingPrehospiInsurerMessage = message[0];
			}));
		}
		this.subs.push(
			this.store.pipe(select(getHospitalization)).subscribe((hospi) => {
				if (hospi === undefined) return;
				this.hospi = hospi;
				if (hospi === null) {
					this.store.dispatch(setPrehospi({ data: undefined }));
					this.store.dispatch(setOverwritePreHospiConfirmed({ data: true }));
					return;
				}

				//If call center
				if (this.insurer.callCenterDeclarationsAllowed && !this.insurer.questionnaireDeclarationsAllowed) {
					this.msgToShow = this.insurer.s5MessageIdWarningNavigation["text" + this.language.languageCode];
				}
				else {
					if (this.insurer.insurerPreHospiAllowContinue === InsurerPreHospiAllowContinueEnum.None) {
						this.store.dispatch(setOverwritePreHospiConfirmed({ data: true }));
					} else {
						this.store.dispatch(
							loadQuestionnaire({
								insurerQuestionnaireId: this.insurer.questionnaireId,
								profileQuestionnaireId: this.contract.profile.questionnaireId ?? 0,
								languageCode: this.language.languageCode,
							})
						);

						if (this.insurer.insurerPreHospiAllowContinue === InsurerPreHospiAllowContinueEnum.Block) {
					
							if (this.existingPrehospiInsurerMessage != null) 
							{
								
								this.store.dispatch(setErrorMsg({ data: this.existingPrehospiInsurerMessage["text" + this.language.languageCode], KioskError: eKioskError.PREHOSPI_EXISTS, noTranslation: true, errorLevel: ErrorLevel.WARNING}));
							} 
							else 
							{
								this.store.dispatch(setErrorMsg({ data: "PREHOSPI_ALREADY_EXISTS_" + InsurerPreHospiAllowContinueEnum.Block, KioskError: eKioskError.PREHOSPI_EXISTS , errorLevel: ErrorLevel.WARNING}));
							}
						} else {
							if (this.existingPrehospiInsurerMessage != null) 
							{
								this.msgToShow = this.existingPrehospiInsurerMessage["text" + this.language.languageCode];
							} 
							else 
							{
								this.msgToShow = this.translateService.instant("PREHOSPI_ALREADY_EXISTS_" + InsurerPreHospiAllowContinueEnum.Warn);
							}
						}
					}
				}
			})
		);

		this.subs.push(
			this.store
				.pipe(select(getQuestionnaire))
				.pipe(filterNullOrUndefined())
				.subscribe((questionnaire) => {
					if (this.hospi == undefined)
						return;
					this.questiontree = [];
					let oldAnswerItemId = 0;
					this.hospi.answers.split(".").forEach((itemId, index) => {
						if (itemId == "0") return;
						const question = questionnaire.listQuestionAnswers.find((el) => el.hierarchyLevel === index && el.type === QuestionType.Question && el.aboveItem === oldAnswerItemId);
						if (question !== undefined) {
							const answer = question.answers.find((a) => a.predItem + 1 == parseInt(itemId));
							this.questiontree.push({ question: question, answer: question.answers.find((a) => a.predItem + 1 == parseInt(itemId)) });
							oldAnswerItemId = answer.item;
						}
					});
				})
		);
	}

	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}

	onClickRetour() {
		//Set light
		switch (this.hospi.light.toUpperCase()) {
			case "R":
				this.lightColor = "red";
				break;
			case "O":
				this.lightColor = "orange";
				break;
			default:
				this.lightColor = "green";
				break;
		}

		this.lightMessage = this.translateService.instant('DONT_FORGET_DISPLAY_CARD_TO_ADMISSION');
		this.displayButtons = false;
		// this.store.dispatch(resetQuestionnaire());
		// this.store.dispatch(setConfirmedSite({ data: false }));
	}

	public onClickOk() {
		if (this.insurer.callCenterDeclarationsAllowed && !this.insurer.questionnaireDeclarationsAllowed) {
			let kiError: eKioskError = environment.kioskMode ? eKioskError.INSURER_KIOSK_NOT_ALLOWED : eKioskError.INSURER_HOME_NOT_ALLOWED;
			this.store.dispatch(setErrorMsg({ data: this.insurer.s5MessageIdContinueNavigation["text" + this.language.languageCode], KioskError: kiError, errorLevel: ErrorLevel.WARNING }));
		}
		else {
			this.store.dispatch(setOverwritePreHospiConfirmed({ data: true }));
			this.store.dispatch(ArcHospiActions.preHospiOverwritten());
		}
	}

	onHome() {
		resetStates(this.store);
	}
}
